import React, { useCallback, useEffect, useState } from 'react';
import userflow from 'userflow.js';
import LegalKitzLogo from '../../../assets/svg/legalkitz-logo.svg?react';
import PromoContracts from '../../../assets/svg/promo-contracts.svg?react';
import PromoEmployment from '../../../assets/svg/promo-employment.svg?react';
import PromoIpProtection from '../../../assets/svg/promo-ip-protection.svg?react';
import PromoStructuring from '../../../assets/svg/promo-structuring.svg?react';
import ListItemBlock from '../../common/list-item-block/ListItemBlock';
import { ROUTES } from '../../common/constants/routes';
import { Box, Button, Grid, List, ListItem, Modal, Typography } from '@mui/material';
import AddIcon from '../../../assets/svg/add-icon.svg?react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TaskItem from '../../common/task-item-block/task-item-block';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { deleteTodo, fetchTodos, updateTodo } from '../../slices/todo/todoSlice';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import TodoForm from './components/TodoForm/TodoForm';
import { ToDoStatus } from '../../common/task-item-block/types';
import { ToDo } from '../../models/todo.model';
import CompanyCreateForm from '../../common/company-create-form';
import { fetchCompanies, selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchEmployees, selectAllEmployees } from '../../slices/employees/employeesSlice';
import { fetchSuppliers } from '../../slices/suppliers/suppliersSlice';
import { fetchCustomers } from '../../slices/customers/customersSlice';
import { fetchDocuments } from '../../slices/documents/documentsSlice';
import { selectUser, selectUserRoleByGroupName } from '../../slices/user/userSlice';
import { RootState } from '../../store';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import { AIDocumentContractModel } from '../../models/ai.document.contract.model';
import { NWClient } from '../../client/NWClient';
import BookACallModal from '../../common/book-a-call-modal/BookACallModal';
import { GROUPS } from '../../common/constants/groups';
import { RolesGate } from '../../common/roles-gate';
import EmployeeDetailsCard from '../../common/employee-details-card/EmployeeDetailsCard';
import { DocumentVaultItemModel } from '../../models/document.vault.item.model';
import { fetchTemplates } from '../../slices/templates/templatesSlice';
import BizCoverBanner from '../../common/bizcover-banner';

const HomePageBlock: React.FC = () => {
  const currentUser = useAppSelector(selectUser);
  const userIsEmployee = Boolean(
    useAppSelector(selectUserRoleByGroupName(GROUPS.COMPANY_EMPLOYEE))
  );
  const templatesLoading = useAppSelector((state: RootState) => state.templates.status);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const todosPending = useAppSelector((state: RootState) => state.todos.status);
  //const fetchingCompanyError = useAppSelector((state: RootState) => state.companies.error);
  const dispatch = useAppDispatch();
  const todos = useAppSelector((state) => state.todos.items);
  const company = useAppSelector(selectAllCompanies)[0];

  const employees = useAppSelector(selectAllEmployees);
  const employee =
    userIsEmployee &&
    employees.find(
      (employee) =>
        employee.email_address?.toLowerCase() === currentUser.email.toLowerCase() ||
        employee.business_email_address?.toLowerCase() === currentUser.email.toLowerCase()
    );
  const [isSkipped, setIsSkipped] = useState(true);
  const [todosLoading, setTodosLoading] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState<
    | {
        id: number;
        message: string;
        due_at: string | null;
        started_at: string | null;
        completed_at: string | null;
      }
    | undefined
  >(undefined);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [documentsWithKeyDates, setDocumentsWithKeyDates] = useState<
    DocumentVaultItemModel[] | AIDocumentContractModel[]
  >([]);
  const [documentsWithKeyDatesOverdue, setDocumentsWithKeyDatesOverdue] = useState<
    DocumentVaultItemModel[] | AIDocumentContractModel[]
  >([]);
  const [showDocsWithKeyDatesOverdue, setShowDocsWithKeyDatesOverdue] = useState<boolean>(false);

  // Same fix as TaskItem's formatDate: Intl's en-GB/en-AU short month for September
  // is "Sept" (4 letters) while every other month is 3, so a fixed table is used
  // instead of relying on the locale's abbreviated month name.
  const MONTHS_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  function formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = MONTHS_SHORT[date.getMonth()];
    return `${day} ${month} ${date.getFullYear()}`;
  }

  const documentsWithKeyDatesData = [
    ...(showDocsWithKeyDatesOverdue ? documentsWithKeyDatesOverdue : documentsWithKeyDates),
  ]
    .slice()
    .sort(function (a, b) {
      return (
        Number(new Date((showDocsWithKeyDatesOverdue ? b : a).key_date_end_date ?? 0)) -
        Number(new Date((showDocsWithKeyDatesOverdue ? a : b).key_date_end_date ?? 0))
      );
    })
    .map((el) => ({
      id: el.id,
      labelText: `${t('labels.endRenewalDate')} ${formatDate(new Date(el.key_date_end_date ?? 0))}`,
      withDate: true,
      name: el.name ?? '',
      onListItemClick:
        'contract_generation' in el
          ? () => navigate(`${ROUTES.DOCUMENT_VAULT}/${el.id}?initialTab=0&contractGenerated=true`)
          : () => navigate(`${ROUTES.DOCUMENT_VAULT}/${el.id}`),
    }));

  const toggleShowBookingModal = () => {
    setIsBookingModalOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isSkipped && company && (todosPending === 'idle' || todosPending === 'failed')) {
      dispatch(fetchTodos());
    }
  }, [isSkipped, company, todosPending, dispatch]);

  const handleSkip = () => {
    setIsSkipped(true);
  };
  const handleAction = (action: string, todo: any) => {
    if (action === 'delete') {
      setTodosLoading(true);
      dispatch(deleteTodo({ id: todo.id }))
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
    if (action === 'modify') {
      setSelectedTodo(todo);
      setOpen(true);
    }
  };
  const handleStart = (todoId: number) => {
    const todo = todos.find((t) => t.id === todoId);
    if (todo) {
      setTodosLoading(true);
      dispatch(
        updateTodo({
          id: todoId,
          data: {
            ...todo,
            started_at: new Date().toISOString(),
          },
        })
      )
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
  };

  const handleComplete = (todoId: number) => {
    const todo = todos.find((t) => t.id === todoId);
    if (todo) {
      setTodosLoading(true);
      dispatch(
        updateTodo({
          id: todoId,
          data: {
            ...todo,
            completed_at: new Date().toISOString(),
          },
        })
      )
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
  };

  const handleContinue = (todoId: number) => {
    const todo = todos.find((t) => t.id === todoId);
    if (todo) {
      setTodosLoading(true);
      dispatch(
        updateTodo({
          id: todoId,
          data: {
            ...todo,
            completed_at: null,
            started_at: new Date().toISOString(),
          },
        })
      )
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
  };

  const getTodoStatus = (todo: ToDo): ToDoStatus => {
    if (todo.completed_at) {
      return 'completed';
    }
    if (!todo.due_at) return 'in_progress';
    const now = new Date();
    const dueDate = new Date(todo.due_at);
    if (now > dueDate) {
      return 'overdue';
    }
    return 'in_progress';
  };
  const sortTodos = (todos: ToDo[]): ToDo[] => {
    const groups: { [key in ToDoStatus]: ToDo[] } = {
      overdue: [],
      in_progress: [],
      completed: [],
    };

    todos.forEach((todo) => {
      const status = getTodoStatus(todo);
      groups[status].push(todo);
    });
    groups.overdue.sort((a, b) => new Date(b.due_at!).getTime() - new Date(a.due_at!).getTime());
    groups.in_progress.sort(
      (a, b) => new Date(a.due_at!).getTime() - new Date(b.due_at!).getTime()
    );
    return [...groups.overdue, ...groups.in_progress, ...groups.completed];
  };

  const handleOpen = () => {
    setSelectedTodo(undefined);
    setOpen(true);
  };
  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      return;
    }
    setOpen(false);
    setSelectedTodo(undefined);
  };

  useEffect(() => {
    const closeHandler = () => setOpen(false);
    document.addEventListener('closeModal', closeHandler);
    return () => {
      document.removeEventListener('closeModal', closeHandler);
    };
  }, []);

  const getCompanyAndProfiles = useCallback(() => {
    if (!company) {
      dispatch(fetchCompanies());
    }
    if (company && company.id !== undefined) {
      dispatch(fetchEmployees({ company_id: company.id }));
      dispatch(fetchSuppliers({ company_id: company.id }));
      dispatch(fetchCustomers({ company_id: company.id }));
    }
  }, [company, dispatch]);

  const getDocuments = useCallback(() => {
    if (currentUser?.id && company) {
      dispatch(fetchDocuments({ data: { user: currentUser.id } }));
      /*dispatch(fetchDocumentContracts({ data: { user: currentUser.id } }))
                                .unwrap()
                                .finally(() => setDocumentContractsLoading(false));*/
    }
  }, [currentUser, dispatch, company]);

  const getTemplates = useCallback(() => {
    if (currentUser?.id && templatesLoading === 'idle') {
      dispatch(fetchTemplates());
    }
  }, [currentUser, templatesLoading, dispatch]);

  const userFlowTrigger = useCallback(() => {
    if (import.meta.env.VITE_USERFLOW_ENABLED === 'TRUE' && currentUser.id) {
      userflow.init(import.meta.env.VITE_USERFLOW_TOKEN);
      const companyPayload = company ? company : null;
      const isCompanyMember = Boolean(
        companyPayload?.members?.some(
          (member) => member?.user_uuid && member.user_uuid === currentUser?.user_uuid
        )
      );
      const isSubscribed = typeof company?.plan_settings?.free === 'object';
      userflow.identify(currentUser.user_uuid, {
        name: currentUser.first_name + ' ' + currentUser.last_name,
        signed_up_at: currentUser.date_joined,
        email: currentUser.email,
        is_company_member: isCompanyMember,
        is_subscribed: isSubscribed,
      });
    }
  }, [
    company,
    currentUser.id,
    currentUser.first_name,
    currentUser.last_name,
    currentUser.email,
    currentUser.date_joined,
    currentUser.user_uuid,
  ]);

  useEffect(() => {
    getCompanyAndProfiles();
  }, [getCompanyAndProfiles]);

  useEffect(() => {
    userFlowTrigger();
  }, [userFlowTrigger]);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);

  useEffect(() => {
    getTemplates();
  }, [getTemplates]);

  useEffect(() => {
    if (currentUser.id) {
      NWClient.list('key-dates-documents', { user: currentUser?.id })
        .then((res: any) => {
          setDocumentsWithKeyDates(res.results);
        })
        .finally(() => {
          NWClient.list('key-dates-documents/expired', { user: currentUser?.id }).then(
            (res: any) => {
              setDocumentsWithKeyDatesOverdue(res.results);
            }
          );
        });
    }
  }, [currentUser]);

  /*useEffect(() => {
    if (
      fetchingCompanyError === 'Request failed with status code 401' ||
      fetchingCompanyError === 'Request failed with status code 500'
    ) {
      window.location.reload();
    }
  }, [fetchingCompanyError]);*/

  return (
    <>
      {!currentUser.id ? (
        <Box sx={{ marginTop: '16px' }}>
          <ProgressOverlay position='static' scrim={false} />
        </Box>
      ) : (
        <>
          <Box className='home-page-wrapper' sx={{ display: 'flex', marginTop: '16px' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  display: 'flex',
                  /*flexGrow: 1,*/ flexDirection: 'column',
                  height: { md: `calc(50% - 8px)` },
                }}
              >
                <Box
                  className='box-with-header home-todos'
                  sx={{
                    position: 'relative',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    className='header-action-row'
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant='h2'>{t('headers.toDo')}</Typography>
                    {company && (
                      <>
                        {!isSkipped && (
                          <Button
                            id='homePageBlockSkipBtn'
                            variant='contained'
                            className='btn-action-basic btn-small'
                            onClick={handleSkip}
                          >
                            Skip
                          </Button>
                        )}
                        {isSkipped && (
                          <Button
                            id='homePageBlockOpenLink'
                            className='add-task-button'
                            startIcon={<AddIcon id='homePageBlockOpenLinkAddIcon' />}
                            onClick={handleOpen}
                          >
                            {t('buttons.addTask')}
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                  {company || userIsEmployee ? (
                    <>
                      {!isSkipped && (
                        <List className='tasks-list' sx={{ paddingTop: 0, paddingBottom: 0 }}>
                          <ListItem>
                            <TaskItem
                              id='homePageBlockUploadAndStoreADocumentTask'
                              taskText={t('homePageTasks.uploadAndStoreDocument')}
                            />
                          </ListItem>
                          <ListItem>
                            <TaskItem
                              id='homePageBlockCreateNewDocumentTask'
                              taskText={t('homePageTasks.createNewDocument')}
                              taskCompleted
                            />
                          </ListItem>
                          <ListItem>
                            <TaskItem
                              id='homePageBlockAddEmployeeTask'
                              taskText={t('homePageTasks.addEmployee')}
                            />
                          </ListItem>
                          <ListItem>
                            <TaskItem
                              id='homePageBlockCreateATemplateFromExistingTask'
                              taskText={t('homePageTasks.createTemplateFromAIComponents')}
                              taskCompleted
                            />
                          </ListItem>
                          <ListItem>
                            <TaskItem
                              id='homePageBlockCreateNewDocumentTask2'
                              taskText={t('homePageTasks.createNewDocument')}
                              taskCompleted
                            />
                          </ListItem>
                          <ListItem>
                            <TaskItem
                              id='homePageBlockAddASupplierOrCustomerTask'
                              taskText={t('homePageTasks.addSupplierOrCustomer')}
                            />
                          </ListItem>
                          <ListItem>
                            <TaskItem
                              id='homePageBlockDocumentSummaryWithAiTask'
                              taskText={t('homePageTasks.documentSummaryWithAI')}
                              taskCompleted
                            />
                          </ListItem>
                          <ListItem>
                            <TaskItem
                              id='homePageBlockStartSubscriptionTask'
                              taskText={t('homePageTasks.startSubscription')}
                            />
                          </ListItem>
                        </List>
                      )}
                      {isSkipped && todos.length === 0 && !todosLoading && (
                        <Box
                          className='empty-state-message-container'
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <div className='empty-state-message'>{t('emptyStateMessages.todos')}</div>
                        </Box>
                      )}
                      {isSkipped && todos.length > 0 && (
                        <List className='tasks-list' sx={{ paddingTop: 0, paddingBottom: 0 }}>
                          {sortTodos(todos).map((todo, index) => (
                            <ListItem key={todo.id}>
                              <span className='task-number'>{index + 1}</span>
                              <TaskItem
                                id={`homePageBlockItemTask_${todo.id}`}
                                taskText={todo.message}
                                date={todo.due_at ? new Date(todo.due_at) : undefined}
                                status={getTodoStatus(todo)}
                                iconLeft
                                onAction={(action) => handleAction(action, todo)}
                                actions
                                onStart={() => handleStart(todo.id)}
                                onComplete={() => handleComplete(todo.id)}
                                onContinue={() => handleContinue(todo.id)}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                      {(todosPending === 'loading' || todosLoading) && (
                        <ProgressOverlay position='absolute' />
                      )}
                    </>
                  ) : (
                    <Box
                      className='empty-state-message-container'
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <CompanyCreateForm />
                    </Box>
                  )}
                </Box>
              </Box>
              <RolesGate group={GROUPS.COMPANY_ADMIN}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                  <Box sx={{ flexGrow: 1, width: '100%' }}>
                    <Box
                      className='box-with-header box-with-header-tabs no-left-right-padding home-key-dates'
                      sx={{ flexGrow: 1 }}
                    >
                      <Box
                        className='header-action-row'
                        sx={{
                          marginBottom: '14px !important',
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant='h2'>{t('headers.keyDates')}</Typography>
                        <Box className='key-dates-tabs'>
                          <Button
                            id='homePageBlockUpcomingBtn'
                            onClick={() => setShowDocsWithKeyDatesOverdue(false)}
                            className={
                              showDocsWithKeyDatesOverdue ? 'list-item-tab' : 'list-item-tab active'
                            }
                          >
                            {t('labels.upcoming')}
                          </Button>
                          <Button
                            id='homePageBlockOverdueBtn'
                            onClick={() => setShowDocsWithKeyDatesOverdue(true)}
                            className={
                              showDocsWithKeyDatesOverdue ? 'list-item-tab active' : 'list-item-tab'
                            }
                          >
                            {t('labels.overdue')}
                          </Button>
                        </Box>
                      </Box>
                      <ListItemBlock
                        id='homePageBlockItemList'
                        listArray={documentsWithKeyDatesData}
                        withMenu={false}
                        emptyStateMessage={t('emptyStateMessages.keyDatesDocuments')}
                        loading={false}
                      />
                    </Box>
                  </Box>
                </Box>
              </RolesGate>
              <RolesGate group={GROUPS.COMPANY_EMPLOYEE}>
                <Box
                  style={{ height: '50%', gap: '16px', flexWrap: 'wrap' }}
                  sx={{
                    display: 'flex',
                    flexGrow: 1,
                    flexDirection: 'column',
                    position: 'relative',
                  }}
                >
                  {employee && company ? (
                    <Grid component='div' size={{ xs: 12 }}>
                      <EmployeeDetailsCard simplified employee={employee} company={company} />
                    </Grid>
                  ) : (
                    <ProgressOverlay position='absolute' />
                  )}
                </Box>
              </RolesGate>
            </Box>
            <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
              <Box
                className='promo-block'
                style={{ height: '100%', minHeight: '290px' /*, maxHeight: '290px'*/ }}
              >
                {/* Logo */}
                <Box className='promo-block__logo'>
                  <LegalKitzLogo />
                </Box>

                {/* Headline */}
                <Typography variant='h2' component='h2' className='promo-block__headline'>
                  {t('legalKitz.promoHeadline')}
                </Typography>

                {/* Subtext */}
                <Typography
                  variant='body2'
                  className='promo-block__subtext'
                  sx={{ whiteSpace: 'pre-line' }}
                >
                  {t('legalKitz.promoSubtext')}
                </Typography>

                {/* Service icons row */}
                <Box className='promo-block__services'>
                  {[
                    { key: 'promoServiceContracts', Icon: PromoContracts },
                    { key: 'promoServiceEmployment', Icon: PromoEmployment },
                    { key: 'promoServiceIP', Icon: PromoIpProtection },
                    { key: 'promoServiceStructuring', Icon: PromoStructuring },
                  ].map(({ key, Icon }) => (
                    <Box key={key} className='promo-block__service-item'>
                      <Icon />
                      <Typography className='promo-block__service-label'>
                        {t(`legalKitz.${key}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTA Button */}
                <Button
                  id='homePageBlockPromoCtaButtonBtn'
                  variant='contained'
                  onClick={() => toggleShowBookingModal()}
                  className='promo-block__cta-button'
                >
                  {t('legalKitz.promoCtaButton')}
                </Button>
              </Box>
              <BizCoverBanner />
            </Box>
          </Box>
          <Modal open={open} onClose={handleClose}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: '8px',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: 'calc(100% - 32px)', sm: '600px' },
                maxHeight: 'calc(100vh - 32px)',
                overflowY: 'auto',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                padding: '16px',
              }}
            >
              {open && <TodoForm existingTodo={selectedTodo} onClose={() => setOpen(false)} />}
            </Box>
          </Modal>
          {isBookingModalOpen && (
            <BookACallModal
              company={company}
              user={currentUser}
              open={isBookingModalOpen}
              onClose={() => toggleShowBookingModal()}
            />
          )}
        </>
      )}
    </>
  );
};

export default HomePageBlock;
